import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Card, Divider, Avatar, LinearProgress } from "@mui/material";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { fetchKpis, fetchRecentActivity, fetchHealthSummary } from "store/slices/dashboardSlice";

// ─── KPI Card Component ──────────────────────────────────────────────────────
function KpiCard({ icon, iconBg, label, value, delta, deltaUp }) {
  return (
    <Card sx={{ height: "100%", border: "1px solid #E8EDF2" }}>
      <MDBox p={2.5} display="flex" alignItems="center" gap={2}>
        <Avatar
          sx={{
            width: 48,
            height: 48,
            backgroundColor: iconBg,
            borderRadius: "12px",
          }}
        >
          <Icon sx={{ color: "#fff", fontSize: "22px !important" }}>{icon}</Icon>
        </Avatar>
        <MDBox flex={1}>
          <MDTypography variant="caption" color="secondary" fontWeight="medium" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
            {label}
          </MDTypography>
          <MDTypography variant="h4" fontWeight="bold" color="dark" sx={{ lineHeight: 1.2, mt: 0.3 }}>
            {value}
          </MDTypography>
        </MDBox>
      </MDBox>
      <Divider sx={{ m: 0 }} />
      <MDBox px={2.5} py={1}>
        <MDTypography variant="caption" color={deltaUp ? "success" : "error"} fontWeight="medium">
          {deltaUp ? "▲" : "▼"} {delta}
          <MDTypography component="span" variant="caption" color="secondary" sx={{ ml: 0.5 }}>
            vs last month
          </MDTypography>
        </MDTypography>
      </MDBox>
    </Card>
  );
}

// ─── Alert Badge ─────────────────────────────────────────────────────────────
function AlertBadge({ severity }) {
  const config = {
    critical: { color: "#E74C3C", bg: "#FADBD8", icon: "error" },
    warning: { color: "#F39C12", bg: "#FDEBD0", icon: "warning" },
    info: { color: "#00ACC4", bg: "#D1F2EB", icon: "info" },
  };
  const c = config[severity] || config.info;
  return (
    <Avatar sx={{ width: 24, height: 24, backgroundColor: c.bg }}>
      <Icon sx={{ color: c.color, fontSize: "14px !important" }}>{c.icon}</Icon>
    </Avatar>
  );
}

// ─── Activity Type Icon ──────────────────────────────────────────────────────
function ActivityIcon({ type }) {
  const config = {
    tenant_created: { icon: "person_add", color: "#27AE60" },
    status_changed: { icon: "swap_horiz", color: "#F39C12" },
    plan_upgraded: { icon: "upgrade", color: "#00ACC4" },
    alert_triggered: { icon: "notifications_active", color: "#E74C3C" },
  };
  const c = config[type] || { icon: "circle", color: "#5D6D7E" };
  return (
    <Avatar sx={{ width: 32, height: 32, backgroundColor: `${c.color}15` }}>
      <Icon sx={{ color: c.color, fontSize: "16px !important" }}>{c.icon}</Icon>
    </Avatar>
  );
}

// ─── Alerts Data ─────────────────────────────────────────────────────────────
const ALERTS = [
  { severity: "critical", tenant: "Initech", msg: "Payment failed — 3rd retry", time: "4m ago" },
  { severity: "warning", tenant: "Dunder Mifflin", msg: "Trial expires in 2 days", time: "1h ago" },
  { severity: "info", tenant: "Umbrella Inc", msg: "Storage at 87% of quota", time: "2h ago" },
];

// ─── Main Component ──────────────────────────────────────────────────────────
function ControlPlaneHome() {
  const dispatch = useDispatch();
  const { kpis, recentActivity, healthSummary } = useSelector((state) => state.dashboard);

  useEffect(() => {
    dispatch(fetchKpis());
    dispatch(fetchRecentActivity());
    dispatch(fetchHealthSummary());
  }, [dispatch]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3} px={3}>
        {/* Page Header */}
        <MDBox mb={3}>
          <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
            <MDTypography variant="caption" color="secondary">Control Plane</MDTypography>
            <MDTypography variant="caption" color="secondary">›</MDTypography>
            <MDTypography variant="caption" fontWeight="bold" color="dark">Home</MDTypography>
          </MDBox>
          <MDBox display="flex" alignItems="center" justifyContent="space-between">
            <MDTypography variant="h4" fontWeight="bold" color="dark">
              Dashboard Overview
            </MDTypography>
            <MDButton variant="gradient" color="info" size="small">
              <Icon>add</Icon>&nbsp;New Tenant
            </MDButton>
          </MDBox>
        </MDBox>

        {/* KPI Cards */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon="apartment"
              iconBg="#27AE60"
              label="Active Tenants"
              value={kpis.activeTenants || 0}
              delta="+12"
              deltaUp={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon="attach_money"
              iconBg="#00ACC4"
              label="Monthly MRR"
              value={`$${((kpis.monthlyRevenue || 0) / 1000).toFixed(1)}K`}
              delta="+8.3%"
              deltaUp={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon="verified"
              iconBg="#2F36CB"
              label="Platform Uptime"
              value="99.97%"
              delta="+0.02%"
              deltaUp={true}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              icon="warning"
              iconBg="#E74C3C"
              label="Open Incidents"
              value={healthSummary.activeIncidents || 0}
              delta={`${healthSummary.critical || 0} critical`}
              deltaUp={false}
            />
          </Grid>
        </Grid>

        {/* Alerts Bar */}
        <Card sx={{ border: "1px solid #E8EDF2", mb: 3 }}>
          <MDBox px={2.5} py={1.5}>
            <MDTypography variant="caption" fontWeight="bold" color="secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.5, fontSize: 11 }}>
              Active Alerts
            </MDTypography>
          </MDBox>
          <Divider sx={{ m: 0 }} />
          <MDBox px={2.5} py={1.5} display="flex" gap={1.5} flexWrap="wrap">
            {ALERTS.map((alert, idx) => (
              <MDBox
                key={idx}
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  backgroundColor: alert.severity === "critical" ? "#FADBD8" : alert.severity === "warning" ? "#FDEBD0" : "#D1F2EB",
                  px: 1.5,
                  py: 0.75,
                  borderRadius: "8px",
                }}
              >
                <AlertBadge severity={alert.severity} />
                <MDTypography variant="caption" fontWeight="bold">{alert.tenant}</MDTypography>
                <MDTypography variant="caption" color="secondary">{alert.msg}</MDTypography>
                <MDTypography variant="caption" color="secondary" sx={{ opacity: 0.6, ml: 0.5 }}>{alert.time}</MDTypography>
              </MDBox>
            ))}
          </MDBox>
        </Card>

        {/* Content Row: Activity + Health */}
        <Grid container spacing={3}>
          {/* Recent Activity */}
          <Grid item xs={12} lg={8}>
            <Card sx={{ border: "1px solid #E8EDF2", height: "100%" }}>
              <MDBox px={2.5} py={2} display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  Recent Activity
                </MDTypography>
                <MDButton variant="text" color="info" size="small">View All</MDButton>
              </MDBox>
              <Divider sx={{ m: 0 }} />
              <MDBox px={2.5} py={1}>
                {(recentActivity || []).length === 0 ? (
                  <MDTypography variant="caption" color="secondary" py={2}>No recent activity</MDTypography>
                ) : (
                  (recentActivity || []).map((item, idx) => (
                    <MDBox
                      key={item.id || idx}
                      display="flex"
                      alignItems="center"
                      gap={1.5}
                      py={1.5}
                      sx={{ borderBottom: idx < (recentActivity || []).length - 1 ? "1px solid #F4F6F9" : "none" }}
                    >
                      <ActivityIcon type={item.type} />
                      <MDBox flex={1}>
                        <MDTypography variant="button" fontWeight="medium" color="dark" display="block" sx={{ fontSize: 13 }}>
                          {item.tenantName}
                        </MDTypography>
                        <MDTypography variant="caption" color="secondary">
                          {item.description}
                        </MDTypography>
                      </MDBox>
                      <MDTypography variant="caption" color="secondary" sx={{ whiteSpace: "nowrap", fontSize: 11 }}>
                        {item.timestamp ? new Date(item.timestamp).toLocaleDateString() : ""}
                      </MDTypography>
                    </MDBox>
                  ))
                )}
              </MDBox>
            </Card>
          </Grid>

          {/* Health Summary */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ border: "1px solid #E8EDF2", height: "100%" }}>
              <MDBox px={2.5} py={2}>
                <MDTypography variant="h6" fontWeight="bold" color="dark">
                  Tenant Health
                </MDTypography>
              </MDBox>
              <Divider sx={{ m: 0 }} />
              <MDBox px={2.5} py={2}>
                {/* Health bars */}
                <HealthRow label="Healthy" count={healthSummary.healthy || 0} total={7} color="#27AE60" />
                <HealthRow label="Warning" count={healthSummary.warning || 0} total={7} color="#F39C12" />
                <HealthRow label="Critical" count={healthSummary.critical || 0} total={7} color="#E74C3C" />
                <HealthRow label="Unknown" count={healthSummary.unknown || 0} total={7} color="#ADB5BD" />

                <Divider sx={{ my: 2 }} />

                {/* Quick stats */}
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="caption" color="secondary">Active Incidents</MDTypography>
                  <MDTypography variant="caption" fontWeight="bold" color="dark">{healthSummary.activeIncidents || 0}</MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="caption" color="secondary">SLA Breaches</MDTypography>
                  <MDTypography variant="caption" fontWeight="bold" color="dark">{healthSummary.slaBreaches || 0}</MDTypography>
                </MDBox>
                <MDBox display="flex" justifyContent="space-between">
                  <MDTypography variant="caption" color="secondary">Last Checked</MDTypography>
                  <MDTypography variant="caption" fontWeight="bold" color="dark">
                    {healthSummary.lastChecked ? new Date(healthSummary.lastChecked).toLocaleTimeString() : "—"}
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

// ─── Health Row Component ────────────────────────────────────────────────────
function HealthRow({ label, count, total, color }) {
  const percent = total > 0 ? (count / total) * 100 : 0;
  return (
    <MDBox mb={1.5}>
      <MDBox display="flex" justifyContent="space-between" mb={0.5}>
        <MDTypography variant="caption" fontWeight="medium" color="dark">{label}</MDTypography>
        <MDTypography variant="caption" fontWeight="bold" color="dark">{count}</MDTypography>
      </MDBox>
      <LinearProgress
        variant="determinate"
        value={percent}
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: "#F4F6F9",
          "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 3 },
        }}
      />
    </MDBox>
  );
}

export default ControlPlaneHome;
